import React, { useState, useEffect, useReducer, useCallback, useContext, useRef } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import {
  CalendarPlus,
  CalendarClock,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Users as UsersIcon,
  ClipboardList,
  X as CloseXIcon,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import HubPageHeader from "../../components/HubPageHeader";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import EventPopover from "../../components/EventPopover";
import EventDetailPopover from "../../components/EventDetailPopover";
import ScheduleDetailPopover from "../../components/ScheduleDetailPopover";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSearch } from "../../context/Search/SearchContext";
import usePlans from "../../hooks/usePlans";
import useTimezone from "../../hooks/useTimezone";

import moment from "moment-timezone";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "moment/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./Schedules.css";

// ─── Custom Year view: calendário anual com 12 mini meses ───
const YearView = ({ date, events = [], onSelectSlot, nowDate }) => {
  const year = date.getFullYear();
  const lang = i18n.language || "pt";
  const monthNames = Array.from({ length: 12 }, (_, idx) =>
    new Date(year, idx, 1).toLocaleDateString(lang, { month: "long" })
  );
  const weekdayLabels = (lang || "pt").startsWith("es")
    ? ["D", "L", "M", "X", "J", "V", "S"]
    : (lang || "pt").startsWith("en")
      ? ["S", "M", "T", "W", "T", "F", "S"]
      : ["D", "S", "T", "Q", "Q", "S", "S"];

  const eventDays = new Set(
    events
      .map((ev) => {
        const d = ev.start instanceof Date ? ev.start : new Date(ev.start);
        if (Number.isNaN(d.getTime())) return null;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      })
      .filter(Boolean)
  );

  const today = nowDate || new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const buildMonthDays = (monthIndex) => {
    const first = new Date(year, monthIndex, 1);
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - first.getDay());

    return Array.from({ length: 42 }, (_, idx) => {
      const current = new Date(gridStart);
      current.setDate(gridStart.getDate() + idx);
      const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;
      return {
        key,
        date: current,
        day: current.getDate(),
        inMonth: current.getMonth() === monthIndex,
        isToday: key === todayKey,
        hasEvent: eventDays.has(key),
      };
    });
  };

  return (
    <div className="rbc-themed-year-calendar">
      {monthNames.map((monthName, monthIndex) => (
        <div key={monthName} className="rbc-themed-year-month-card">
          <div className="rbc-themed-year-month-title">
            {monthName}
          </div>
          <div className="rbc-themed-year-weekdays">
            {weekdayLabels.map((label, idx) => (
              <span key={`${label}-${idx}`}>{label}</span>
            ))}
          </div>
          <div className="rbc-themed-year-days">
            {buildMonthDays(monthIndex).map((day) => (
              <button
                key={day.key}
                type="button"
                className={[
                  "rbc-themed-year-day",
                  day.inMonth ? "" : "is-out",
                  day.isToday ? "is-today" : "",
                  day.hasEvent ? "has-event" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => onSelectSlot && onSelectSlot({ start: day.date })}
              >
                <span>{day.day}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
YearView.title = (date) => `${date.getFullYear()}`;
YearView.navigate = (date, action) => {
  if (action === "PREV") return new Date(date.getFullYear() - 1, 0, 1);
  if (action === "NEXT") return new Date(date.getFullYear() + 1, 0, 1);
  return date;
};
YearView.range = (date) => {
  return [new Date(date.getFullYear(), 0, 1), new Date(date.getFullYear(), 11, 31)];
};

function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const buildCalendarMessages = () => ({
  date: i18n.t("scheduleModal.calendar.messages.date"),
  time: i18n.t("scheduleModal.calendar.messages.time"),
  event: i18n.t("scheduleModal.calendar.messages.event"),
  allDay: i18n.t("scheduleModal.calendar.messages.allDay"),
  week: i18n.t("scheduleModal.calendar.messages.week"),
  work_week: i18n.t("scheduleModal.calendar.messages.work_week"),
  day: i18n.t("scheduleModal.calendar.messages.day"),
  month: i18n.t("scheduleModal.calendar.messages.month"),
  previous: i18n.t("scheduleModal.calendar.messages.previous"),
  next: i18n.t("scheduleModal.calendar.messages.next"),
  yesterday: i18n.t("scheduleModal.calendar.messages.yesterday"),
  tomorrow: i18n.t("scheduleModal.calendar.messages.tomorrow"),
  today: i18n.t("scheduleModal.calendar.messages.today"),
  agenda: i18n.t("scheduleModal.calendar.messages.agenda"),
  noEventsInRange: i18n.t("scheduleModal.calendar.messages.noEventsInRange"),
  showMore: (total) => "+" + total + " " + i18n.t("scheduleModal.calendar.messages.showMore"),
});

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];
    schedules.forEach((schedule) => {
      const idx = state.findIndex((s) => s.id === schedule.id);
      if (idx !== -1) state[idx] = schedule;
      else newSchedules.push(schedule);
    });
    return [...state, ...newSchedules];
  }
  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const idx = state.findIndex((s) => s.id === schedule.id);
    if (idx !== -1) {
      state[idx] = schedule;
      return [...state];
    }
    return [schedule, ...state];
  }
  if (action.type === "DELETE_SCHEDULE") {
    const id = action.payload;
    const idx = state.findIndex((s) => s.id === id);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
  return state;
};

// ─── Reducer separado pra eventos do calendário ───
const eventsReducer = (state, action) => {
  if (action.type === "LOAD_EVENTS") {
    const events = action.payload;
    const newEvents = [];
    events.forEach((event) => {
      const idx = state.findIndex((e) => e.id === event.id);
      if (idx !== -1) state[idx] = event;
      else newEvents.push(event);
    });
    return [...state, ...newEvents];
  }
  if (action.type === "UPDATE_EVENT") {
    const event = action.payload;
    const idx = state.findIndex((e) => e.id === event.id);
    if (idx !== -1) {
      state[idx] = event;
      return [...state];
    }
    return [event, ...state];
  }
  if (action.type === "DELETE_EVENT") {
    const id = action.payload;
    const idx = state.findIndex((e) => e.id === id);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
  return state;
};

// ─── Custom Toolbar (tematizado dark/light com setas + tabs de view) ───
const ChatiaCalendarToolbar = ({ label, onNavigate, onView, view, views = [], date }) => {
  const viewLabels = {
    month: i18n.t("schedules.calendar.viewModes.month") || "Mês",
    week: i18n.t("schedules.calendar.viewModes.week") || "Semana",
    day: i18n.t("schedules.calendar.viewModes.day") || "Dia",
    year: i18n.t("schedules.calendar.viewModes.year") || "Ano",
    agenda: i18n.t("schedules.calendar.viewModes.schedule") || "Agenda",
  };

  // Detecta se a view atual já contém "hoje" — controla a exibição do botão "Hoje"
  const isOnToday = React.useMemo(() => {
    if (!date) return true;
    const now = new Date();
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (view === "day") return sameDay(date, now);
    if (view === "month") return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
    if (view === "year") return date.getFullYear() === now.getFullYear();
    if (view === "week") {
      // RBC passa a data inicial da semana; checamos se hoje está dentro de [date, date+7)
      const start = new Date(date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      return now >= start && now < end;
    }
    if (view === "agenda") {
      // Agenda mostra ~30 dias a partir de date
      const start = new Date(date);
      const end = new Date(start);
      end.setDate(end.getDate() + 30);
      return now >= start && now < end;
    }
    return true;
  }, [date, view]);

  return (
    <div className="rbc-themed-toolbar">
      <div className="rbc-themed-nav">
        <button
          type="button"
          className="rbc-themed-nav-btn"
          onClick={() => onNavigate("PREV")}
          aria-label="prev"
        >
          <ChevronLeft size={16} strokeWidth={2} />
        </button>
        <div className="rbc-themed-nav-label">{label}</div>
        <button
          type="button"
          className="rbc-themed-nav-btn"
          onClick={() => onNavigate("NEXT")}
          aria-label="next"
        >
          <ChevronRight size={16} strokeWidth={2} />
        </button>
        {!isOnToday && (
          <button
            type="button"
            className="rbc-themed-today-btn"
            onClick={() => onNavigate("TODAY")}
            aria-label="today"
          >
            {i18n.t("schedules.calendar.today") || "Hoje"}
          </button>
        )}
      </div>
      <div className="rbc-themed-views">
        {views.map((v) => (
          <button
            key={v}
            type="button"
            className={`rbc-themed-view-btn${view === v ? " is-active" : ""}`}
            onClick={() => onView(v)}
          >
            {viewLabels[v] || v}
          </button>
        ))}
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  return {
    mainPaper: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(2),
      overflow: "hidden",
      borderRadius: ds.radius?.md ?? 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
    },
    calendarWrap: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "auto",
      ...theme.scrollbarStyles,
    },
    mobileHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
    },
    addBtn: {
      borderRadius: 12,
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    eventBadge: {
      borderRadius: 6,
      padding: "2px 6px",
      fontSize: 12,
      fontWeight: 600,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    searchInput: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
    },
    // Dialog "ver mais" — mesmo padrão dos outros dialogs do sistema
    showMoreDialogPaper: {
      borderRadius: "21px 21px 12px 12px !important",
      background: `${isDark ? "#121212" : "#ffffff"} !important`,
      backgroundImage: "none !important",
      overflow: "hidden !important",
      "& .MuiDialogContent-root": {
        background: isDark ? "#121212" : "#ffffff",
      },
    },
  };
});

const Schedules = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  // Schedules (mensagens agendadas)
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { ticketSearchParam: searchParam, setTicketSearchParam: setSearchParam } = useSearch();
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));

  // Calendar Events (agenda — reuniões/tarefas)
  const [events, dispatchEvents] = useReducer(eventsReducer, []);
  const [eventPopoverAnchor, setEventPopoverAnchor] = useState(null);
  const [eventPopoverInitialDate, setEventPopoverInitialDate] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [eventDetailAnchor, setEventDetailAnchor] = useState(null);
  const [scheduleDetail, setScheduleDetail] = useState(null);
  const [scheduleDetailAnchor, setScheduleDetailAnchor] = useState(null);
  const eventBtnRef = useRef(null);
  const dayFlashTimerRef = useRef(null);
  const [showMoreDialog, setShowMoreDialog] = useState({ open: false, date: null, events: [] });
  const [calendarView, setCalendarView] = useState(isMobile ? "agenda" : "month");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [timeScrollVersion, setTimeScrollVersion] = useState(0);
  const [nowTick, setNowTick] = useState(0);
  const [flashingDay, setFlashingDay] = useState(null);

  const { getPlanCompany } = usePlans();
  const { getCompanyTimezone } = useTimezone();
  const [companyTimezone, setCompanyTimezone] = useState(null);
  const [calendarLang, setCalendarLang] = useState(i18n.language);
  const canViewOtherUsersAgenda = user?.profile === "admin" || user?.allUserChat === "enabled";
  const canSeeAgendaItem = useCallback((item) => {
    if (canViewOtherUsersAgenda) return true;
    return Number(item?.userId) === Number(user?.id);
  }, [canViewOtherUsersAgenda, user?.id]);

  // CSS variables que alimentam ./Schedules.css
  const themeVars = {
    "--rbc-surface": isDark ? "#11141b" : "#ffffff",
    "--rbc-surface-elev": isDark ? "#171b24" : "#f3f4f6",
    "--rbc-surface-hover": isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
    "--rbc-border": isDark ? "#202632" : "#e8e8e8",
    "--rbc-border-light": isDark ? "#1a1f29" : "#f0f0f0",
    "--rbc-text": isDark ? "#e9edef" : "#111b21",
    "--rbc-text-secondary": isDark ? "#8696a0" : "#667781",
    "--rbc-text-muted": isDark ? "#566069" : "#a0a8b1",
    "--rbc-primary": theme.palette.primary.main,
    "--rbc-primary-dark": theme.palette.primary.dark || theme.palette.primary.main,
    "--rbc-primary-contrast": theme.palette.primary.contrastText || "#fff",
    // Sobretom sutil baseado no próprio tema (não na cor primary)
    "--rbc-primary-tint": isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
    // Cor das listras diagonais nos dias fora do mês corrente — bem suaves
    "--rbc-stripe-color": isDark ? "rgba(255,255,255,0.012)" : "rgba(15,23,42,0.022)",
    "--rbc-scrollbar-thumb": isDark ? "rgba(255,255,255,0.2)" : "rgba(140,120,100,0.3)",
    "--rbc-scrollbar-thumb-hover": isDark ? "rgba(255,255,255,0.35)" : "rgba(140,120,100,0.5)",
    "--rbc-scrollbar-arrow-up": isDark
      ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 1L1 6h6z' fill='rgba(255%2C255%2C255%2C0.2)'/%3E%3C/svg%3E")`
      : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 1L1 6h6z' fill='rgba(140%2C120%2C100%2C0.35)'/%3E%3C/svg%3E")`,
    "--rbc-scrollbar-arrow-down": isDark
      ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 7L1 2h6z' fill='rgba(255%2C255%2C255%2C0.2)'/%3E%3C/svg%3E")`
      : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 7L1 2h6z' fill='rgba(140%2C120%2C100%2C0.35)'/%3E%3C/svg%3E")`,
  };

  useEffect(() => {
    const handleLangChange = (lng) => setCalendarLang(lng);
    i18n.on("languageChanged", handleLangChange);
    return () => i18n.off("languageChanged", handleLangChange);
  }, []);

  const localizer = React.useMemo(() => momentLocalizer(moment), [calendarLang]);
  const calendarMessages = React.useMemo(() => buildCalendarMessages(), [calendarLang]);
  const getCompanyNowDate = useCallback(() => {
    const tz = companyTimezone || "America/Sao_Paulo";
    const m = moment().tz(tz);
    return new Date(
      m.year(),
      m.month(),
      m.date(),
      m.hour(),
      m.minute(),
      m.second(),
      m.millisecond()
    );
  }, [companyTimezone, nowTick]);
  const calendarScrollToTime = React.useMemo(
    () => {
      const now = getCompanyNowDate();
      const d = new Date(now);
      d.setHours(d.getHours() - 4);
      if (d.getDate() !== now.getDate()) {
        d.setHours(0, 0, 0, 0);
      }
      return d;
    },
    [getCompanyNowDate, timeScrollVersion]
  );
  const calendarMinTime = React.useMemo(() => {
    const d = getCompanyNowDate();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [getCompanyNowDate]);
  const calendarMaxTime = React.useMemo(() => {
    const d = getCompanyNowDate();
    d.setHours(23, 59, 59, 999);
    return d;
  }, [getCompanyNowDate]);
  const getCalendarNow = useCallback(() => getCompanyNowDate(), [getCompanyNowDate]);

  const formatDayKey = useCallback((date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  useEffect(() => {
    if (isMobile && !["month", "agenda"].includes(calendarView)) {
      setCalendarView("agenda");
    }
  }, [isMobile, calendarView]);

  useEffect(() => {
    return () => {
      if (dayFlashTimerRef.current) clearTimeout(dayFlashTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNowTick((prev) => prev + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  const focusCurrentTime = useCallback(() => {
    setTimeScrollVersion((prev) => prev + 1);
  }, []);

  const handleCalendarViewChange = useCallback((nextView) => {
    setCalendarView(nextView);
    if (nextView === "day" || nextView === "week") {
      setCalendarDate(getCompanyNowDate());
      focusCurrentTime();
    }
  }, [focusCurrentTime, getCompanyNowDate]);

  const handleCalendarNavigate = useCallback((nextDate) => {
    setCalendarDate(nextDate);
    if (calendarView === "day" || calendarView === "week") {
      focusCurrentTime();
    }
  }, [calendarView, focusCurrentTime]);

  useEffect(() => {
    async function loadTz() {
      try {
        const tzData = await getCompanyTimezone(user.companyId);
        setCompanyTimezone(tzData.timezone);
      } catch {
        setCompanyTimezone("America/Sao_Paulo");
      }
    }
    loadTz();
  }, [user.companyId]);

  const utcToCompanyDate = (utcDateStr) => {
    const tz = companyTimezone || "America/Sao_Paulo";
    const m = moment.utc(utcDateStr).tz(tz);
    return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
  };

  // Constrói Date a partir de date (YYYY-MM-DD) + startTime (HH:mm) do CalendarEvent
  const eventToDate = (ev) => {
    if (!ev?.date) return new Date();
    const [y, m, d] = ev.date.split("-").map(Number);
    if (!ev.startTime || ev.allDay) return new Date(y, (m || 1) - 1, d || 1, 9, 0);
    const [h, min] = ev.startTime.split(":").map(Number);
    return new Date(y, (m || 1) - 1, d || 1, h || 0, min || 0);
  };

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, user.companyId);
      if (!planConfigs || !planConfigs.plan || !planConfigs.plan.useSchedules) {
        toast.error(i18n.t("scheduleModal.permissions.noAccess"));
        setTimeout(() => history.push(`/`), 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar-events", { params: { searchParam } });
      dispatchEvents({ type: "RESET" });
      dispatchEvents({ type: "LOAD_EVENTS", payload: data.events || [] });
    } catch (err) {
      // Silencia se backend ainda não tiver a rota — não bloqueia a tela
    }
  }, [searchParam]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      fetchSchedules();
      fetchEvents();
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber, contactId, fetchSchedules, fetchEvents]);

  // Socket — schedules
  useEffect(() => {
    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        if (canSeeAgendaItem(data.schedule)) {
          dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
        } else if (data.schedule?.id) {
          dispatch({ type: "DELETE_SCHEDULE", payload: +data.schedule.id });
        }
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    };
    socket.on(`company${user.companyId}-schedule`, onCompanySchedule);
    return () => socket.off(`company${user.companyId}-schedule`, onCompanySchedule);
  }, [socket, user.companyId, canSeeAgendaItem]);

  // Socket — calendar events
  useEffect(() => {
    const onCompanyEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        if (canSeeAgendaItem(data.event)) {
          dispatchEvents({ type: "UPDATE_EVENT", payload: data.event });
        } else if (data.event?.id) {
          dispatchEvents({ type: "DELETE_EVENT", payload: +data.event.id });
        }
      }
      if (data.action === "delete") {
        dispatchEvents({ type: "DELETE_EVENT", payload: +data.eventId });
      }
    };
    socket.on(`company${user.companyId}-calendar-event`, onCompanyEvent);
    return () => socket.off(`company${user.companyId}-calendar-event`, onCompanyEvent);
  }, [socket, user.companyId, canSeeAgendaItem]);

  const cleanContact = () => setContactId("");
  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };
  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };
  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleDetail(null);
    setScheduleDetailAnchor(null);
    setScheduleModalOpen(true);
  };
  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setPageNumber(1);
    dispatch({ type: "RESET" });
    await fetchSchedules();
  };

  const handleOpenScheduleDetail = (schedule, anchor) => {
    setScheduleDetail(schedule);
    setScheduleDetailAnchor(anchor);
  };

  const handleDeleteScheduleFromDetail = (schedule) => {
    setDeletingSchedule(schedule);
    setConfirmModalOpen(true);
  };

  // ─── Calendar Events handlers ───
  const handleOpenNewEvent = () => {
    setEditingEvent(null);
    setEventPopoverInitialDate(new Date());
    setEventPopoverAnchor(eventBtnRef.current);
  };
  const handleSelectCalendarSlot = useCallback((slotInfo) => {
    if (!["month", "year"].includes(calendarView) || !slotInfo?.start) return;

    const clickedDate = new Date(slotInfo.start);
    clickedDate.setHours(0, 0, 0, 0);
    setFlashingDay(formatDayKey(clickedDate));
    if (dayFlashTimerRef.current) clearTimeout(dayFlashTimerRef.current);
    dayFlashTimerRef.current = setTimeout(() => setFlashingDay(null), 650);

    setEditingEvent(null);
    setEventDetail(null);
    setEventDetailAnchor(null);
    setScheduleDetail(null);
    setScheduleDetailAnchor(null);
    setEventPopoverInitialDate(clickedDate);
    setEventPopoverAnchor(eventBtnRef.current);
  }, [calendarView, formatDayKey]);
  const handleCloseEventPopover = () => {
    setEventPopoverAnchor(null);
    setEditingEvent(null);
    setEventPopoverInitialDate(null);
  };
  const handleSaveEvent = async (eventData) => {
    try {
      if (editingEvent?.id) {
        await api.put(`/calendar-events/${editingEvent.id}`, eventData);
        toast.success(i18n.t("schedules.toasts.eventUpdated") || "Agenda atualizada");
      } else {
        await api.post("/calendar-events", eventData);
        toast.success(i18n.t("schedules.toasts.eventCreated") || "Agenda criada");
      }
      await fetchEvents();
    } catch (err) {
      toastError(err);
    }
  };
  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/calendar-events/${eventId}`);
      toast.success(i18n.t("schedules.toasts.eventDeleted") || "Agenda excluída");
      setEventDetail(null);
      setEventDetailAnchor(null);
      await fetchEvents();
    } catch (err) {
      toastError(err);
    }
  };
  const handleOpenEventDetail = (event, anchor) => {
    setEventDetail(event);
    setEventDetailAnchor(anchor);
  };
  const handleEditEventFromDetail = () => {
    setEditingEvent(eventDetail);
    setEventPopoverInitialDate(eventToDate(eventDetail));
    setEventPopoverAnchor(eventDetailAnchor);
    setEventDetail(null);
  };

  const handleEditEvent = (event, anchor) => {
    setEditingEvent(event);
    setEventPopoverInitialDate(eventToDate(event));
    setEventPopoverAnchor(anchor || eventBtnRef.current);
    setEventDetail(null);
    setEventDetailAnchor(null);
  };

  const loadMore = () => setPageNumber((p) => p + 1);
  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  // ─── Cores e ícones por tipo de card ───
  const COLORS = {
    scheduleProgramada: "#f97316", // laranja — mensagem ainda não enviada
    scheduleEnviada: "#16a34a",    // verde — mensagem já enviada
    scheduleErro: "#ef4444",       // vermelho — erro no envio
    reuniao: "#2563eb",            // azul (reunião)
    tarefa: "#f59e0b",             // amarelo/âmbar (tarefa)
  };
  const colorForSchedule = (s) => {
    if (s?.status === "ENVIADA") return COLORS.scheduleEnviada;
    if (s?.status === "ERRO") return COLORS.scheduleErro;
    return COLORS.scheduleProgramada;
  };

  // ─── Eventos do calendário (juntando schedules + calendar events) ───
  const calendarEvents = [
    // Mensagens agendadas — roxo + ícone MessageSquare
    ...schedules.map((schedule) => ({
      kind: "schedule",
      raw: schedule,
      title: (
        <div className={`event-container${isMobile ? " event-mobile" : ""}`}>
          <MessageSquare size={12} strokeWidth={2} style={{ flexShrink: 0, marginRight: 4 }} />
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, fontWeight: 600 }}>
            {schedule?.contact?.name || "—"}
          </span>
          <Pencil
            size={13}
            strokeWidth={1.8}
            className="edit-icon"
            onClick={(e) => { e.stopPropagation(); handleEditSchedule(schedule); }}
            style={{ cursor: "pointer" }}
          />
          <Trash2
            size={13}
            strokeWidth={1.8}
            className="delete-icon"
            onClick={(e) => {
              e.stopPropagation();
              setDeletingSchedule(schedule);
              setConfirmModalOpen(true);
            }}
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
      start: utcToCompanyDate(schedule.sendAt),
      end: utcToCompanyDate(schedule.sendAt),
      eventColor: colorForSchedule(schedule),
    })),
    // Eventos de agenda — reunião (azul + Users) ou tarefa (âmbar + ClipboardList)
    ...events.map((ev) => {
      const isTarefa = ev.eventType === "tarefa";
      const color = isTarefa ? COLORS.tarefa : COLORS.reuniao;
      const Icon = isTarefa ? ClipboardList : UsersIcon;
      return {
        kind: "event",
        raw: ev,
        title: (
          <div className={`event-container${isMobile ? " event-mobile" : ""}`}>
            <Icon size={12} strokeWidth={2} style={{ flexShrink: 0, marginRight: 4 }} />
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, fontWeight: 600 }}>
              {ev.title}
            </span>
            <Pencil
              size={13}
              strokeWidth={1.8}
              className="edit-icon"
              onClick={(e) => { e.stopPropagation(); handleEditEvent(ev, e.currentTarget); }}
              style={{ cursor: "pointer" }}
            />
            <Trash2
              size={13}
              strokeWidth={1.8}
              className="delete-icon"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteEvent(ev.id);
              }}
              style={{ cursor: "pointer" }}
            />
          </div>
        ),
        start: eventToDate(ev),
        end: ev.duration && !ev.allDay
          ? new Date(eventToDate(ev).getTime() + ev.duration * 60000)
          : eventToDate(ev),
        allDay: ev.allDay,
        eventColor: color,
      };
    }),
  ];

  const YearCalendarView = React.useMemo(() => {
    const View = (props) => (
      <YearView
        {...props}
        onSelectSlot={handleSelectCalendarSlot}
        nowDate={getCompanyNowDate()}
      />
    );
    View.title = YearView.title;
    View.navigate = YearView.navigate;
    View.range = YearView.range;
    return View;
  }, [handleSelectCalendarSlot, getCompanyNowDate]);

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingSchedule && i18n.t("schedules.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          scheduleId={selectedSchedule ? selectedSchedule.id : null}
          contactId={contactId}
          cleanContact={cleanContact}
        />
      )}

      <EventPopover
        open={Boolean(eventPopoverAnchor)}
        anchorEl={eventPopoverAnchor}
        side="left"
        onClose={handleCloseEventPopover}
        onSave={handleSaveEvent}
        initialDate={eventPopoverInitialDate}
        editEvent={editingEvent}
      />

      <EventDetailPopover
        open={Boolean(eventDetailAnchor) && Boolean(eventDetail)}
        anchorEl={eventDetailAnchor}
        event={eventDetail}
        onClose={() => { setEventDetail(null); setEventDetailAnchor(null); }}
        onEdit={handleEditEventFromDetail}
        onDelete={() => eventDetail && handleDeleteEvent(eventDetail.id)}
      />

      <ScheduleDetailPopover
        open={Boolean(scheduleDetailAnchor) && Boolean(scheduleDetail)}
        anchorEl={scheduleDetailAnchor}
        schedule={scheduleDetail}
        timezone={companyTimezone}
        onClose={() => { setScheduleDetail(null); setScheduleDetailAnchor(null); }}
        onEdit={handleEditSchedule}
        onDelete={handleDeleteScheduleFromDetail}
      />

      {/* Dialog "ver mais" — exibe todos os eventos do dia clicado */}
      <Dialog
        open={showMoreDialog.open}
        onClose={() => setShowMoreDialog({ open: false, date: null, events: [] })}
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.showMoreDialogPaper }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 16px",
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText || "#fff",
          }}
        >
          <Typography style={{ fontWeight: 600, fontSize: 15 }}>
            {showMoreDialog.date
              ? new Date(showMoreDialog.date).toLocaleDateString(i18n.language, {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })
              : ""}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowMoreDialog({ open: false, date: null, events: [] })}
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.10)",
              color: "#ffffff",
              padding: 6,
            }}
          >
            <CloseXIcon size={18} />
          </IconButton>
        </div>
        <DialogContent style={{ padding: 12, ...theme.scrollbarStyles }}>
          {(showMoreDialog.events || []).length === 0 ? (
            <Typography style={{ padding: 16, textAlign: "center", color: isDark ? "#8696a0" : "#5b6470", fontSize: 13 }}>
              {i18n.t("scheduleModal.calendar.messages.noEventsInRange")}
            </Typography>
          ) : (
            (showMoreDialog.events || [])
              .slice()
              .sort((a, b) => (a.start?.getTime?.() || 0) - (b.start?.getTime?.() || 0))
              .map((ev, idx) => {
                const isEvent = ev.kind === "event";
                const Icon = isEvent
                  ? (ev.raw?.eventType === "tarefa" ? ClipboardList : UsersIcon)
                  : MessageSquare;
                const time = ev.start
                  ? `${String(ev.start.getHours()).padStart(2, "0")}:${String(ev.start.getMinutes()).padStart(2, "0")}`
                  : "—";
                const title = isEvent
                  ? ev.raw?.title
                  : ev.raw?.contact?.name || "—";
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => {
                      setShowMoreDialog({ open: false, date: null, events: [] });
                      if (isEvent) {
                        handleOpenEventDetail(ev.raw, e.currentTarget);
                      } else {
                        handleOpenScheduleDetail(ev.raw, e.currentTarget);
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      padding: "10px 12px",
                      marginBottom: 6,
                      borderRadius: 10,
                      border: "none",
                      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
                      borderLeft: `3px solid ${ev.eventColor || theme.palette.primary.main}`,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)";
                    }}
                  >
                    <span style={{ color: ev.eventColor, flexShrink: 0, display: "flex" }}>
                      <Icon size={16} strokeWidth={1.8} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: isDark ? "#e9edef" : "#111b21",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {title}
                      </span>
                      <span style={{
                        display: "block",
                        fontSize: 11.5,
                        color: isDark ? "#8696a0" : "#5b6470",
                        marginTop: 1,
                      }}>
                        {time}
                      </span>
                    </span>
                  </button>
                );
              })
          )}
        </DialogContent>
      </Dialog>

      <HubPageHeader
        icon={<CalendarClock size={20} strokeWidth={1.8} />}
        title={i18n.t("schedules.title")}
        subtitle={
          <>
            {schedules.length} {i18n.t("schedules.subtitleCount") || "mensagens agendadas"}
            {events.length > 0 && <> · {events.length} {i18n.t("schedules.subtitleEvents") || "agendamentos"}</>}
          </>
        }
        action={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="primary"
              ref={eventBtnRef}
              onClick={handleOpenNewEvent}
              className={classes.addBtn}
              startIcon={<CalendarPlus size={16} strokeWidth={1.8} />}
              style={isMobile ? { padding: "6px 10px" } : undefined}
            >
              {isMobile ? (i18n.t("schedules.buttons.addEventShort") || "Agenda") : (i18n.t("schedules.buttons.addEvent") || "Criar agenda")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenScheduleModal}
              className={classes.addBtn}
              startIcon={<CalendarClock size={16} strokeWidth={1.8} />}
              style={isMobile ? { padding: "6px 10px" } : undefined}
            >
              {isMobile ? i18n.t("schedules.buttons.addShort") : i18n.t("schedules.buttons.add")}
            </Button>
          </div>
        }
      />

      <Paper className={classes.mainPaper} variant="outlined" elevation={0}>
        <div className={classes.calendarWrap} style={themeVars} onScroll={handleScroll}>
          <Calendar
            key={`${companyTimezone}-${calendarView}-${timeScrollVersion}`}
            className={`rbc-themed${calendarView === "year" ? " rbc-themed-year-mode" : ""}`}
            messages={calendarMessages}
            components={{
              toolbar: ChatiaCalendarToolbar,
              month: {
                dateHeader: ({ date, label }) => (
                  <button
                    type="button"
                    className={`rbc-button-link${formatDayKey(date) === flashingDay ? " rbc-date-flash" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectCalendarSlot({ start: date });
                    }}
                  >
                    {label}
                  </button>
                ),
              },
            }}
            formats={{
              agendaDateFormat: "DD/MM ddd",
              weekdayFormat: isMobile ? "ddd" : "dddd",
            }}
            defaultView={isMobile ? "agenda" : "month"}
            views={
              isMobile
                ? { month: true, agenda: true }
                : { month: true, week: true, day: true, year: YearCalendarView, agenda: true }
            }
            localizer={localizer}
            events={calendarEvents}
            date={calendarDate}
            onNavigate={handleCalendarNavigate}
            selectable="ignoreEvents"
            view={calendarView}
            onView={handleCalendarViewChange}
            scrollToTime={calendarScrollToTime}
            min={calendarMinTime}
            max={calendarMaxTime}
            getNow={getCalendarNow}
            onSelectSlot={handleSelectCalendarSlot}
            dayPropGetter={(date) =>
              formatDayKey(date) === flashingDay
                ? { className: "rbc-day-flash" }
                : {}
            }
            onSelectEvent={(ev, syntheticEvt) => {
              const target = syntheticEvt?.currentTarget || syntheticEvt?.target || eventBtnRef.current;
              if (ev.kind === "event") {
                handleOpenEventDetail(ev.raw, target);
              } else if (ev.kind === "schedule") {
                handleOpenScheduleDetail(ev.raw, target);
              }
            }}
            onShowMore={(evts, date) => {
              setShowMoreDialog({ open: true, date, events: evts || [] });
            }}
            // Bloqueia o drill-down nativo do react-big-calendar.
            // - doShowMoreDrillDown=false: "+N mais" não navega pra Day
            // - getDrilldownView=null: clicar no número do dia também não navega
            // - onDrillDown vazio: garantia adicional contra qualquer navegação interna
            doShowMoreDrillDown={false}
            getDrilldownView={() => null}
            onDrillDown={() => {}}
            eventPropGetter={(ev) => ({
              style: {
                backgroundColor: ev.eventColor || theme.palette.primary.main,
                borderColor: ev.eventColor || theme.palette.primary.main,
              },
            })}
            startAccessor="start"
            endAccessor="end"
            style={{ flex: 1, minHeight: 480 }}
          />
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Schedules;
